import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '../../../redux/actions';
import useMarketingLiveRefresh from './useMarketingLiveRefresh';

const PAGE_SIZE = 20;
const SCROLL_BOTTOM_THRESHOLD = 80; // px จากขอบล่างที่เริ่มโหลดหน้าถัดไป

// infinite scroll แทน pagination แบบกดปุ่ม — loadPage(pageToLoad, append) ต่อแถวเข้าไป
// ใน rows เดิมถ้า append=true, แทนที่ทั้งหมดถ้า false (ตอน mount/ค้นหา/เปลี่ยน filter)
export function useOpportunities() {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const hasLoadedRef = useRef(false);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState('');
  // ค่าเริ่มต้น = สถานะที่ยังไม่จบงาน (ไม่รวม WON/LOST/CANCELLED) — reset กลับมาเป็นชุดนี้เองทุกครั้ง
  // ที่ mount ใหม่ (refresh หน้าจอ) เพราะเป็นแค่ useState เฉยๆ ไม่ persist ที่ไหน
  const [statusFilter, setStatusFilter] = useState(['WAITING', 'IN_PROGRESS', 'ON_HOLD']);
  const [typeFilter, setTypeFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [contactFilter, setContactFilter] = useState(null); // { _id/id, name } — เลือกจาก autocomplete
  const [dueDateFrom, setDueDateFrom] = useState('');
  const [dueDateTo, setDueDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const buildParams = useCallback((pageToLoad, size) => ({
    q: q || undefined,
    status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
    type: typeFilter || undefined,
    ownerId: ownerFilter || undefined,
    contactId: (contactFilter?._id || contactFilter?.id) || undefined,
    quotationDueDateFrom: dueDateFrom || undefined,
    quotationDueDateTo: dueDateTo || undefined,
    sortBy,
    sortOrder,
    page: pageToLoad,
    size,
  }), [q, statusFilter, typeFilter, ownerFilter, contactFilter, dueDateFrom, dueDateTo, sortBy, sortOrder]);

  const loadPage = useCallback(async (pageToLoad, append) => {
    if (append) setLoadingMore(true);
    else if (!hasLoadedRef.current) setLoading(true);
    try {
      const data = await dispatch(actions.listOpportunities(buildParams(pageToLoad, PAGE_SIZE)));
      setRows((prev) => (append ? [...prev, ...(data.data || [])] : (data.data || [])));
      setTotal(data.total || 0);
      setPage(pageToLoad);
      hasLoadedRef.current = true;
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [dispatch, buildParams]);

  // ค้นหา/เปลี่ยน filter/เปลี่ยนการเรียง — debounce แล้วเริ่มใหม่จากหน้า 1 เสมอ
  useEffect(() => {
    const t = setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      loadPage(1, false);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter, typeFilter, ownerFilter, contactFilter, dueDateFrom, dueDateTo, sortBy, sortOrder]);

  const hasMore = rows.length < total;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < SCROLL_BOTTOM_THRESHOLD) {
      loadPage(page + 1, true);
    }
  };

  const changeQuery = (value) => setQ(value);
  const toggleStatusFilter = (status) => setStatusFilter((prev) => (
    prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
  ));

  // รีเฟรชเฉพาะจำนวนแถวที่โหลดมาแล้ว (หน้า 1..page ปัจจุบัน) แทนที่จะรีเซ็ตกลับหน้า 1
  // ล้วน ๆ — กันไม่ให้แถวที่เลื่อนโหลดเพิ่มมาก่อนหน้านี้หายไปตอนแก้ไข/เปลี่ยนสถานะ
  const fetchVisible = useCallback(async (silent) => {
    try {
      const data = await dispatch(actions.listOpportunities({ ...buildParams(1, PAGE_SIZE * page), silent }));
      const next = data.data || [];
      // ข้อมูลเท่าเดิม (เช่นดึงเงียบๆ เป็นระยะแล้วไม่มีใครแก้) → ไม่ set ใหม่ ตารางจะไม่ re-render โดยเปล่าประโยชน์
      setRows((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      setTotal(data.total || 0);
    } catch {
      // เงียบไว้ — error หลัก ๆ ของแต่ละ action ที่เรียก refresh จะโชว์ผ่าน global error dialog อยู่แล้ว
    }
  }, [dispatch, buildParams, page]);
  const refreshVisible = useCallback(() => fetchVisible(false), [fetchVisible]);

  // เห็นการแก้จากที่อื่นเอง (popup/แท็บอื่น/ผู้ใช้อื่น) — ข้ามตอนกำลังโหลดครั้งแรก/กำลังเลื่อนโหลดเพิ่ม
  // กันชนกับ loadPage ที่ต่อแถวอยู่ และใช้โหมด silent ไม่ให้เด้ง dialog error ทุกรอบถ้าเน็ตหลุด
  useMarketingLiveRefresh(() => fetchVisible(true), { enabled: !loading && !loadingMore });

  // เปลี่ยนสถานะแบบอิสระตรงจากตาราง list — ไม่ผ่านการ validate ลำดับ workflow
  // ฝั่ง backend ก็เปิดให้เปลี่ยนได้ทุกสถานะแล้ว (ดู opportunity.controller.js)
  const changeStatus = async (id, toStatus) => {
    try {
      await dispatch(actions.changeOpportunityStatus({ id, toStatus }));
    } finally {
      refreshVisible();
    }
  };

  return {
    rows,
    total,
    loading,
    loadingMore,
    hasMore,
    q,
    statusFilter,
    typeFilter,
    ownerFilter,
    contactFilter,
    dueDateFrom,
    dueDateTo,
    sortBy,
    sortOrder,
    changeQuery,
    toggleStatusFilter,
    setStatusFilter,
    setTypeFilter,
    setOwnerFilter,
    setContactFilter,
    setDueDateFrom,
    setDueDateTo,
    setSortBy,
    setSortOrder,
    changeStatus,
    scrollRef,
    handleScroll,
    refetch: refreshVisible,
  };
}

export default useOpportunities;
